import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type UploadStatus =
  | 'idle'
  | 'validating'
  | 'uploading'
  | 'processing'
  | 'complete'
  | 'error'
  | 'duplicate';

export interface PartProgress {
  partNumber: number;
  uploaded: boolean;
  etag?: string;
}

export interface MultipartState {
  mediaId: string;
  uploadId: string;
  filename: string;
  totalParts: number;
  parts: PartProgress[];
  /** Bytes uploaded so far */
  bytesUploaded: number;
  totalBytes: number;
}

export interface UploadItem {
  /** Client-side temporary ID before mediaId is assigned */
  clientId: string;
  mediaId: string | null;
  filename: string;
  contentType: string;
  fileSize: number;
  status: UploadStatus;
  progress: number;
  error: string | null;
  /** Present when a duplicate is detected */
  duplicate: {
    mediaId: string;
    filename: string;
    uploadedAt: string;
    author: string;
    folderPath: string[];
  } | null;
  /** Present for multipart uploads */
  multipart: MultipartState | null;
}

export interface UploadState {
  /** Active and recently completed uploads, keyed by clientId */
  uploads: Record<string, UploadItem>;
  /** Whether the upload panel is open */
  panelOpen: boolean;
}

const initialState: UploadState = {
  uploads: {},
  panelOpen: false,
};

const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    addUpload(state, action: PayloadAction<UploadItem>) {
      state.uploads[action.payload.clientId] = action.payload;
    },
    updateUploadStatus(
      state,
      action: PayloadAction<{ clientId: string; status: UploadStatus; progress?: number; error?: string }>,
    ) {
      const upload = state.uploads[action.payload.clientId];
      if (upload) {
        upload.status = action.payload.status;
        if (action.payload.progress !== undefined) {
          upload.progress = action.payload.progress;
        }
        if (action.payload.error !== undefined) {
          upload.error = action.payload.error;
        }
      }
    },
    setUploadMediaId(state, action: PayloadAction<{ clientId: string; mediaId: string }>) {
      const upload = state.uploads[action.payload.clientId];
      if (upload) {
        upload.mediaId = action.payload.mediaId;
      }
    },
    setUploadDuplicate(
      state,
      action: PayloadAction<{ clientId: string; duplicate: UploadItem['duplicate'] }>,
    ) {
      const upload = state.uploads[action.payload.clientId];
      if (upload) {
        upload.duplicate = action.payload.duplicate;
        upload.status = 'duplicate';
      }
    },
    setMultipartState(
      state,
      action: PayloadAction<{ clientId: string; multipart: MultipartState }>,
    ) {
      const upload = state.uploads[action.payload.clientId];
      if (upload) {
        upload.multipart = action.payload.multipart;
      }
    },
    updatePartProgress(
      state,
      action: PayloadAction<{ clientId: string; partNumber: number; etag: string }>,
    ) {
      const upload = state.uploads[action.payload.clientId];
      if (upload?.multipart) {
        const part = upload.multipart.parts.find((p) => p.partNumber === action.payload.partNumber);
        if (part) {
          part.uploaded = true;
          part.etag = action.payload.etag;
        }
        const uploadedBytes = upload.multipart.parts.filter((p) => p.uploaded).length;
        upload.multipart.bytesUploaded =
          (uploadedBytes / upload.multipart.totalParts) * upload.multipart.totalBytes;
        upload.progress = Math.round((uploadedBytes / upload.multipart.totalParts) * 100);
      }
    },
    removeUpload(state, action: PayloadAction<string>) {
      delete state.uploads[action.payload];
    },
    clearCompletedUploads(state) {
      for (const clientId of Object.keys(state.uploads)) {
        if (state.uploads[clientId].status === 'complete') {
          delete state.uploads[clientId];
        }
      }
    },
    setPanelOpen(state, action: PayloadAction<boolean>) {
      state.panelOpen = action.payload;
    },
  },
});

export const {
  addUpload,
  updateUploadStatus,
  setUploadMediaId,
  setUploadDuplicate,
  setMultipartState,
  updatePartProgress,
  removeUpload,
  clearCompletedUploads,
  setPanelOpen,
} = uploadSlice.actions;

// Selectors
export const selectUploads = (state: { upload: UploadState }) => state.upload.uploads;
export const selectUploadList = (state: { upload: UploadState }) =>
  Object.values(state.upload.uploads);
export const selectActiveUploads = (state: { upload: UploadState }) =>
  Object.values(state.upload.uploads).filter(
    (u) => u.status === 'uploading' || u.status === 'processing' || u.status === 'validating',
  );
export const selectPanelOpen = (state: { upload: UploadState }) => state.upload.panelOpen;

export default uploadSlice.reducer;
